import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchWalletCharges,
  fetchWalletUser,
} from "../modules/wallet/services/walletService";
import "../css/WalletCharge.css";
import "../css/AdManagement.css";
import Layout from "./Layout";

const PAGE_SIZE = 30;

const STATUS_LABEL = {
  NOT_ISSUED: "발급 미완료",
  CANCELED: "취소",
  WAITING_DEPOSIT: "입금대기",
  DEPOSIT_CONFIRMED: "입금완료",
  EXPIRED: "만료",
};

const STATUS_CLASS = {
  NOT_ISSUED: "wallet-status-issued",
  CANCELED: "wallet-status-canceled",
  WAITING_DEPOSIT: "wallet-status-waiting",
  DEPOSIT_CONFIRMED: "wallet-status-confirmed",
  EXPIRED: "wallet-status-expired",
};

function formatDateTime(str) {
  if (!str) return "-";
  const d = new Date(str);
  if (isNaN(d)) return str;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const EMPTY_FILTER = {
  userQuery: "",
  chargeAccountId: "",
  status: "",
  expireFrom: "",
  expireTo: "",
};

function WalletChargeManagement() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({ ...EMPTY_FILTER });
  const [tempFilter, setTempFilter] = useState({ ...EMPTY_FILTER });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const result = await fetchWalletCharges({
          ...filter,
          page,
          size: PAGE_SIZE,
        });
        if (!cancelled) {
          setList(result.list || []);
          setTotalCount(result.totalCount || 0);
        }
      } catch {
        if (!cancelled) {
          setList([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [filter, page]);

  async function handleUserClick(memberNo) {
    try {
      const result = await fetchWalletUser(memberNo);
      navigate(`/wallet/user/${result.memberNo}`, {
        state: { prefetched: result },
      });
    } catch {
      alert("사용자 정보를 찾을 수 없습니다.");
    }
  }

  function handleSearch() {
    setFilter({ ...tempFilter });
    setPage(1);
  }

  function handleReset() {
    setTempFilter({ ...EMPTY_FILTER });
    setFilter({ ...EMPTY_FILTER });
    setPage(1);
  }

  function handleTempChange(e) {
    const { name, value } = e.target;
    setTempFilter((prev) => ({ ...prev, [name]: value }));
  }

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const startIndex = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, totalCount);

  return (
    <Layout>
      <div className="wallet-charge">
        <h4 style={{ fontWeight: 700, color: "#1f2937", margin: 0 }}>
          충전/입금 관리
        </h4>

        <div className="wallet-charge-filter">
          <input
            type="text"
            name="userQuery"
            placeholder="사용자 검색"
            value={tempFilter.userQuery}
            onChange={handleTempChange}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            style={{ width: 160 }}
          />
          <input
            type="text"
            name="chargeAccountId"
            placeholder="가상계좌 ID"
            value={tempFilter.chargeAccountId}
            onChange={handleTempChange}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            style={{ width: 140 }}
          />
          <select
            name="status"
            value={tempFilter.status}
            onChange={handleTempChange}
            style={{ width: 130 }}
          >
            <option value="">전체</option>
            <option value="NOT_ISSUED">발급 미완료</option>
            <option value="WAITING_DEPOSIT">입금대기</option>
            <option value="DEPOSIT_CONFIRMED">입금완료</option>
            <option value="EXPIRED">만료</option>
            <option value="CANCELED">취소</option>
          </select>
          <input
            type="date"
            name="expireFrom"
            className="wallet-date-input"
            value={tempFilter.expireFrom}
            onChange={handleTempChange}
          />
          <span style={{ fontSize: "0.85rem", color: "#6b7280" }}>~</span>
          <input
            type="date"
            name="expireTo"
            className="wallet-date-input"
            value={tempFilter.expireTo}
            onChange={handleTempChange}
          />
          <button
            className="wallet-filter-action-btn wallet-filter-search"
            onClick={handleSearch}
          >
            검색
          </button>
          <button
            className="wallet-filter-action-btn wallet-filter-reset"
            onClick={handleReset}
          >
            초기화
          </button>
        </div>

        <div className="wallet-charge-table-section">
          {loading ? (
            <div className="text-center text-muted py-4">불러오는 중...</div>
          ) : list.length === 0 ? (
            <div className="wallet-charge-empty">조회된 데이터가 없습니다</div>
          ) : (
            <>
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>가상계좌 ID</th>
                    <th>은행/계좌번호</th>
                    <th>사용자</th>
                    <th>신청금액</th>
                    <th>입금금액</th>
                    <th>상태</th>
                    <th>만료일시</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((item) => (
                    <tr key={item.chargeAccountId}>
                      <td>{item.chargeAccountId}</td>
                      <td>
                        {item.vaBank} {item.vaNum}
                      </td>
                      <td>
                        <button
                          onClick={() => handleUserClick(item.memberNo)}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            color: "#6366f1",
                            cursor: "pointer",
                            fontSize: "inherit",
                            fontWeight: 600,
                            textDecoration: "underline",
                          }}
                        >
                          {item.userName}({item.memberNo})
                        </button>
                      </td>
                      <td>
                        {item.requestedAmount != null
                          ? item.requestedAmount.toLocaleString() + " 원"
                          : "-"}
                      </td>
                      <td>
                        {item.depositAmount != null
                          ? item.depositAmount.toLocaleString() + " 원"
                          : "-"}
                      </td>
                      <td>
                        <span
                          className={`wallet-status-badge ${STATUS_CLASS[item.status] || ""}`}
                        >
                          {STATUS_LABEL[item.status] || item.status}
                        </span>
                      </td>
                      <td>{formatDateTime(item.expireAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="pagination-info">
                <div>
                  총 {totalCount}개 항목 중 {startIndex} - {endIndex}
                </div>
                <div className="pagination">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    이전
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i + 1}
                      className={page === i + 1 ? "active" : ""}
                      onClick={() => setPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={endIndex >= totalCount}
                  >
                    다음
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default WalletChargeManagement;
